package org.teddapp.project.ui.objectives

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.flow.flowOf
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.PillarTabs
import org.teddapp.project.ui.nav.NavIntent
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsScreen

/**
 * Écran Objectifs unique. La logique vit dans [ObjectivesModel] ; la mise en
 * page s'adapte à la largeur. Éditeur, gestion des catégories, confirmation
 * de suppression et réglages sont écrits une fois ; seule leur entrée en
 * scène change, glissement latéral en étendu, plein écran en compact.
 */
@Composable
fun ObjectivesScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val model = rememberObjectivesModel(repos)
    LaunchedEffect(Unit) {
        org.teddapp.project.ui.nav.NavIntent.consumeObjective()?.let { model.detailObjectiveId = it }
    }
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        when (wc) {
            WindowClass.Compact -> ObjectivesCompactLayout(model, repos, onNavigate)
            else -> ObjectivesExpandedLayout(model, repos, onNavigate, showSidebar = wc == WindowClass.Expanded)
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun ObjectivesExpandedLayout(
    model: ObjectivesModel,
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit,
    showSidebar: Boolean,
) {
    var settingsOpen by remember { mutableStateOf(false) }
    val filtered = model.filtered

    Box(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            Row(modifier = Modifier.fillMaxSize()) {
                TeddNavRail(active = Section.Objectives, onNavigate = onNavigate, onSettingsClick = { settingsOpen = true })
                if (showSidebar) {
                    Sidebar(
                        categories = model.categories,
                        allCount = model.objectives.size,
                        activeCount = model.activeCount,
                        selectedCat = model.selectedCat,
                        onSelect = { model.selectedCat = it },
                        categoryCount = { catId -> model.objectives.count { it.category_id == catId } },
                        onManageCategories = { model.categoryManagerOpen = true },
                    )
                }
                Column(modifier = Modifier.fillMaxSize().weight(1f)) {
                    PillarTabs(current = Section.Objectives, onNavigate = onNavigate, compact = false)
                    MainHeader(
                        selectedCat = model.selectedCat,
                        categories = model.categories,
                        count = filtered.size,
                        search = model.search,
                        onSearchChange = { model.search = it },
                        viewMode = model.viewMode,
                        onViewModeChange = { model.viewMode = it },
                        sortMode = model.sortMode,
                        onSortChange = { model.sortMode = it },
                        onNew = { model.openEditor() },
                    )
                    if (model.skills.isNotEmpty()) {
                        ObjectivesSkillChips(
                            skills = model.skills,
                            selectedSkill = model.selectedSkill,
                            countFor = { id -> model.objectives.count { it.skill_id == id } },
                            onSelect = { model.selectedSkill = it },
                            modifier = Modifier.padding(start = 24.dp, end = 24.dp, top = 10.dp),
                            onOpenSkill = { NavIntent.openSkill(it); onNavigate(Section.Skills) },
                        )
                    }
                    if (!showSidebar) {
                        Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                            ObjectivesCategoryChips(
                                categories = model.categories,
                                selectedCat = model.selectedCat,
                                countAll = model.objectives.size,
                                onSelect = { model.selectedCat = it },
                                onManage = { model.categoryManagerOpen = true },
                            )
                        }
                    }
                    YearAndTabsRow(
                        years = model.years,
                        selectedYear = model.selectedYear,
                        onSelectYear = { model.selectedYear = it },
                        selectedTab = model.selectedTab,
                        onSelectTab = { model.selectedTab = it },
                        activeCount = model.activeCountForYear,
                        doneCount = model.doneCountForYear,
                        allCount = model.objectivesForYear.size,
                    )
                    if (filtered.isEmpty()) {
                        EmptyStateBlock(hasSearch = model.search.isNotBlank(), onNew = { model.openEditor() })
                    } else when (model.viewMode) {
                        ViewMode.List -> LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(start = 28.dp, end = 28.dp, top = 20.dp, bottom = 80.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                        ) {
                            items(filtered, key = { it.id }) { obj ->
                                ObjectiveListRow(
                                    objective = obj,
                                    category = model.categoryOf(obj),
                                    expanded = obj.id in model.expanded,
                                    repos = repos,
                                    onToggleExpand = { model.toggleExpand(obj.id) },
                                    onEdit = { model.openEditor(obj.id) },
                                    onDelete = { model.confirmDeleteId = obj.id },
                                    onToggleTask = { model.toggleTask(it) },
                                    onSendToPomodoro = { model.sendTaskToPomodoro(it) },
                                    skill = model.skills.firstOrNull { it.id == obj.skill_id },
                                    onOpenSkill = { NavIntent.openSkill(it); onNavigate(Section.Skills) },
                                )
                            }
                        }
                        ViewMode.Grid -> LazyVerticalGrid(
                            columns = GridCells.Adaptive(260.dp),
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(start = 28.dp, end = 28.dp, top = 20.dp, bottom = 80.dp),
                            verticalArrangement = Arrangement.spacedBy(14.dp),
                            horizontalArrangement = Arrangement.spacedBy(14.dp),
                        ) {
                            items(filtered, key = { it.id }) { obj ->
                                ObjectiveGridCard(
                                    objective = obj,
                                    category = model.categoryOf(obj),
                                    onOpen = { model.detailObjectiveId = obj.id },
                                    onEdit = { model.openEditor(obj.id) },
                                    onDelete = { model.confirmDeleteId = obj.id },
                                )
                            }
                        }
                        ViewMode.Cards -> LazyVerticalGrid(
                            columns = GridCells.Adaptive(440.dp),
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = PaddingValues(start = 28.dp, end = 28.dp, top = 20.dp, bottom = 80.dp),
                            verticalArrangement = Arrangement.spacedBy(18.dp),
                            horizontalArrangement = Arrangement.spacedBy(18.dp),
                        ) {
                            items(filtered, key = { it.id }) { obj ->
                                ObjectiveHeroCard(
                                    objective = obj,
                                    category = model.categoryOf(obj),
                                    repos = repos,
                                    onOpen = { model.detailObjectiveId = obj.id },
                                    onEdit = { model.openEditor(obj.id) },
                                    onDelete = { model.confirmDeleteId = obj.id },
                                    onToggleTask = { model.toggleTask(it) },
                                )
                            }
                        }
                    }
                }
            }
        }

        // Éditeur : glissement depuis la droite
        Scrim(visible = model.editorState !is ObjectiveEditorState.Closed, onDismiss = { model.closeEditor() })
        AnimatedVisibility(
            visible = model.editorState !is ObjectiveEditorState.Closed,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) { ObjectiveEditorHost(model, repos, fullScreen = false) }

        // Détail : glissement depuis la droite
        Scrim(visible = model.detailObjectiveId != null, onDismiss = { model.detailObjectiveId = null })
        AnimatedVisibility(
            visible = model.detailObjectiveId != null,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) {
            val detail = model.objective(model.detailObjectiveId)
            if (detail != null) {
                ObjectiveDetailPanel(
                    objective = detail,
                    category = model.categoryOf(detail),
                    repos = repos,
                    onClose = { model.detailObjectiveId = null },
                    onEdit = { val id = model.detailObjectiveId; model.detailObjectiveId = null; if (id != null) model.openEditor(id) },
                    onDelete = { val id = model.detailObjectiveId; model.detailObjectiveId = null; if (id != null) model.confirmDeleteId = id },
                    onToggleTask = { model.toggleTask(it) },
                    onSendToPomodoro = { model.sendTaskToPomodoro(it) },
                    skill = model.skills.firstOrNull { it.id == detail.skill_id },
                    onOpenSkill = { model.detailObjectiveId = null; NavIntent.openSkill(it); onNavigate(Section.Skills) },
                    onOpenMindMap = { model.detailObjectiveId = null; NavIntent.openMindMap(it); onNavigate(Section.MindMaps) },
                )
            }
        }

        // Réglages : tiroir latéral
        Scrim(visible = settingsOpen, onDismiss = { settingsOpen = false })
        AnimatedVisibility(
            visible = settingsOpen,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) { SettingsScreen(onClose = { settingsOpen = false }, onOpenBackup = { settingsOpen = false; onNavigate(Section.Backup) }) }

        ObjectivesDialogs(model, repos)
        TeddSnackbarHost(state = model.snackbar)
    }
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun ObjectivesCompactLayout(model: ObjectivesModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    var searchOpen by remember { mutableStateOf(false) }
    var settingsOpen by remember { mutableStateOf(false) }
    val filtered = model.filtered

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            LazyColumn(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                // Assez de marge pour que le bouton flottant ne couvre pas la dernière carte.
                contentPadding = PaddingValues(bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                item("header") {
                    ObjectivesCompactHeader(
                        searchOpen = searchOpen,
                        search = model.search,
                        onSearchToggle = { searchOpen = !searchOpen; if (!searchOpen) model.search = "" },
                        onSearchChange = { model.search = it },
                    )
                }
                item("pillar") { PillarTabs(current = Section.Objectives, onNavigate = onNavigate, compact = true) }
                item("hero") {
                    ObjectivesCompactHero(
                        avgProgress = model.avgProgress,
                        totalCount = model.objectives.size,
                        activeCount = model.activeCount,
                        doneCount = model.doneCount,
                    )
                }
                item("years") {
                    YearAndTabsRow(
                        years = model.years,
                        selectedYear = model.selectedYear,
                        onSelectYear = { model.selectedYear = it },
                        selectedTab = model.selectedTab,
                        onSelectTab = { model.selectedTab = it },
                        activeCount = model.activeCountForYear,
                        doneCount = model.doneCountForYear,
                        allCount = model.objectivesForYear.size,
                    )
                }
                if (model.skills.isNotEmpty()) {
                    item("skills") {
                        ObjectivesSkillChips(
                            skills = model.skills,
                            selectedSkill = model.selectedSkill,
                            countFor = { id -> model.objectives.count { it.skill_id == id } },
                            onSelect = { model.selectedSkill = it },
                            modifier = Modifier.padding(horizontal = 16.dp),
                            onOpenSkill = { NavIntent.openSkill(it); onNavigate(Section.Skills) },
                        )
                    }
                }
                item("categories") {
                    ObjectivesCategoryChips(
                        categories = model.categories,
                        selectedCat = model.selectedCat,
                        countAll = model.objectives.size,
                        onSelect = { model.selectedCat = it },
                        onManage = { model.categoryManagerOpen = true },
                    )
                }
                if (filtered.isEmpty()) {
                    item("empty") { ObjectivesCompactEmpty(hasSearch = model.search.isNotBlank(), onNew = { model.openEditor() }) }
                } else {
                    items(filtered, key = { it.id }) { obj ->
                        ObjectivesCompactRow(
                            objective = obj,
                            category = model.categoryOf(obj),
                            expanded = obj.id in model.expanded,
                            repos = repos,
                            onToggleExpand = { model.toggleExpand(obj.id) },
                            onEdit = { model.openEditor(obj.id) },
                            onDelete = { model.confirmDeleteId = obj.id },
                            onToggleTask = { model.toggleTask(it) },
                            onSendToPomodoro = { model.sendTaskToPomodoro(it) },
                            skill = model.skills.firstOrNull { it.id == obj.skill_id },
                            onOpenSkill = { NavIntent.openSkill(it); onNavigate(Section.Skills) },
                        )
                    }
                }
            }
            TeddBottomNav(active = Section.Objectives, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }

        FabAdd(modifier = Modifier.align(Alignment.BottomEnd).padding(end = 20.dp, bottom = 88.dp), onClick = { model.openEditor() })

        // Éditeur : plein écran depuis le bas
        AnimatedVisibility(visible = model.editorState !is ObjectiveEditorState.Closed, enter = fadeIn(tween(180)), exit = fadeOut(tween(180))) {
            Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.35f)))
        }
        AnimatedVisibility(
            visible = model.editorState !is ObjectiveEditorState.Closed,
            enter = slideInVertically(tween(280)) { it },
            exit = slideOutVertically(tween(220)) { it },
        ) { ObjectiveEditorHost(model, repos, fullScreen = true) }

        // Réglages : plein écran depuis le bas
        AnimatedVisibility(visible = settingsOpen, enter = fadeIn(tween(180)), exit = fadeOut(tween(180))) {
            Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.35f)))
        }
        AnimatedVisibility(visible = settingsOpen, enter = slideInVertically(tween(280)) { it }, exit = slideOutVertically(tween(220)) { it }) {
            SettingsScreen(onClose = { settingsOpen = false }, onOpenBackup = { settingsOpen = false; onNavigate(Section.Backup) }, modifier = Modifier.fillMaxSize())
        }

        ObjectivesDialogs(model, repos)
        TeddSnackbarHost(state = model.snackbar)
    }
}

// ──────────────────────────────────────────────────────────────────────
// Partagé : éditeur, dialogues, voile
// ──────────────────────────────────────────────────────────────────────

/** L'éditeur avec sa logique d'enregistrement, écrite une fois. */
@Composable
private fun ObjectiveEditorHost(model: ObjectivesModel, repos: TeddRepositories, fullScreen: Boolean) {
    val initial = (model.editorState as? ObjectiveEditorState.Edit)?.let { model.objective(it.id) }
    val initialMilestones by (
        if (initial != null) repos.milestones.observeByObjective(initial.id) else flowOf(emptyList())
        ).collectAsState(emptyList())
    ObjectiveEditor(
        initial = initial,
        initialMilestones = initialMilestones,
        categories = model.categories,
        skills = model.skills,
        onClose = { model.closeEditor() },
        onSave = { draft -> model.saveObjective(initial, initialMilestones, draft) },
        onDelete = {
            val id = (model.editorState as? ObjectiveEditorState.Edit)?.id
            model.closeEditor()
            if (id != null) model.confirmDeleteId = id
        },
        modifier = if (fullScreen) Modifier.fillMaxSize() else Modifier,
    )
}

@Composable
private fun ObjectivesDialogs(model: ObjectivesModel, repos: TeddRepositories) {
    if (model.categoryManagerOpen) {
        CategoryManagerDialog(
            categories = model.categories,
            repository = repos.categories,
            onClose = { model.categoryManagerOpen = false },
        )
    }
    model.confirmDeleteId?.let { id ->
        val toDelete = model.objective(id)
        if (toDelete == null) {
            model.confirmDeleteId = null
        } else {
            ConfirmDeleteDialog(
                objective = toDelete,
                onCancel = { model.confirmDeleteId = null },
                onConfirm = { model.deleteObjective(toDelete) },
            )
        }
    }
}

@Composable
private fun Scrim(visible: Boolean, onDismiss: () -> Unit) {
    AnimatedVisibility(visible = visible, enter = fadeIn(tween(180)), exit = fadeOut(tween(180))) {
        Box(
            modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.32f))
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onDismiss),
        )
    }
}
